import { DefaultHttpExceptionType } from "http-repository-cache";
import type { KybPersonVerification, KybComplementRequest, KybRisk, KybRiskConfig, KybRiskConfigView, KybRiskSnapshot, KybScreeningSummary } from "@/core/kyb/entities/KybCase";
import KybCaseRepository from "@/core/kyb/repositories/KybCaseRepository";
import { KybDirectorFallback } from "@/core/kyb/entities/KybOwnership";
import {
  KybCaseAuditEntry,
  KybCaseDecisionInput,
  KybCaseDecisionOutcome,
  KybCaseDecisionResult,
  KybCaseDetail,
  KybSubmissionReadiness,
  KybCaseStatus,
  KybCaseTimeline,
  KybCbsOpening,
  KybCbsOpeningState,
  KybCbsPushStatus,
  KybComplementRequestInput,
  KybDecisionBlocker,
  KybDecisionEligibility,
  KybDecisionOption,
  KybInstructionState,
  KybMfaChallenge,
  KYB_ENTITY_HIT_ID,
  KybAuditFieldOption,
  KybAuditFilters,
  KybAssignableAnalysts,
  KybInstructionSummary,
  KybComplementDocumentType,
  KybScreeningCandidate,
  KybScreeningHit,
  KybScreeningQualificationInput,
  KybSubmissionBlocker,
  KybSubmissionOutcome,
  KybValidationInput,
  KybValidationResult,
} from "@/core/kyb/entities/KybCase";
import {
  RepositoryResult,
  RepositoryResultFactory,
} from "@/core/shared/types/RepositoryResult";
import { HttpClient } from "../../http/HttpClient";
import { ApiResponse } from "../../api/types/ApiResponse";
import {
  executeKybRequest,
  toKybException,
} from "../../http/services/kybRequest/kybRequest";
import { ApiResponseHandler } from "../../http/services/handleApiResponse/handleApiResponse";
import kybEndpoints from "../../api/kybEndpoints/kybEndpoints";

type KybCaseDto = {
  caseId: string;
  countryCode: string;
  legalFormCode: string;
  statut: KybCaseStatus;
  version: number;
  createdBy: string | null;
  createdAt: string | null;
  submittedAt: string | null;
  submittedBy: string | null;
  beNonVerifie: boolean;
  structureOpaque: boolean;
  readyForValidation: boolean;
  slaPausedAt: string | null;
  slaResumedAt: string | null;
  cbsOpeningState: KybCbsOpeningState | null;
  eddRequired?: boolean;
  eddRiskAssessment?: string | null;
};

type KybSubmissionDto = {
  caseId: string;
  statut: KybCaseStatus;
  alreadySubmitted: boolean;
  beNonVerifie: boolean;
  structureOpaque: boolean;
  missingMandatoryDocuments: number;
  submittedAt: string | null;
  submittedBy: string | null;
};

type KybInstructionDto = {
  caseId: string;
  statut: KybCaseStatus;
  readyForValidation: boolean;
  readyBy: string | null;
  slaPausedAt: string | null;
  slaResumedAt: string | null;
};

type KybValidationDto = {
  caseId: string;
  statut: KybCaseStatus;
  validatedBy: string;
  validatedAt: string | null;
  alreadyValidated: boolean;
};

type KybScreeningHitDto = {
  relatedPersonId: string;
  displayName: string;
  legalParty: boolean;
  outcome: string;
  provider: string | null;
  detail: string | null;
  screenedAt: string | null;
  qualified: boolean;
  candidates?: KybScreeningCandidateDto[] | null;
};

type KybScreeningCandidateDto = {
  reference: string;
  fullName: string;
  birthDate?: string | null;
  nationality?: string | null;
  matchScore?: number | null;
  listTypes?: string[] | null;
  pep: boolean;
  sanction: boolean;
  category?: string | null;
  residenceCountry?: string | null;
  placeOfBirth?: string | null;
  associates?: string[] | null;
  sources?: string[] | null;
  suggested?: boolean | null;
};

const mapScreeningCandidate = (dto: KybScreeningCandidateDto): KybScreeningCandidate => ({
  reference: dto.reference,
  fullName: dto.fullName,
  birthDate: dto.birthDate ?? undefined,
  nationality: dto.nationality ?? undefined,
  matchScore: dto.matchScore ?? undefined,
  listTypes: dto.listTypes ?? [],
  pep: dto.pep,
  sanction: dto.sanction,
  category: dto.category ?? undefined,
  residenceCountry: dto.residenceCountry ?? undefined,
  placeOfBirth: dto.placeOfBirth ?? undefined,
  associates: dto.associates ?? [],
  sources: dto.sources ?? [],
  suggested: dto.suggested ?? false,
});

const mapScreeningHit = (dto: KybScreeningHitDto): KybScreeningHit => ({
  relatedPersonId: dto.relatedPersonId,
  displayName: dto.displayName,
  legalParty: dto.legalParty,
  outcome: dto.outcome,
  provider: dto.provider ?? undefined,
  detail: dto.detail ?? undefined,
  screenedAt: dto.screenedAt ?? undefined,
  qualified: dto.qualified,
  candidates: (dto.candidates ?? []).map(mapScreeningCandidate),
});

type KybEntityHitDto = Omit<KybScreeningHitDto, "relatedPersonId" | "legalParty">;

const mapEntityHit = (dto: KybEntityHitDto): KybScreeningHit => ({
  ...mapScreeningHit({ ...dto, relatedPersonId: KYB_ENTITY_HIT_ID, legalParty: false }),
  entity: true,
});

type KybMfaChallengeDto = {
  sentTo: string;
  ttlMinutes: number;
  testCode?: string;
};

type KybEligibilityDto = {
  blockers: { code: KybDecisionBlocker; label: string }[];
  allowedOptions: KybDecisionOption[];
};

type KybDecisionDto = {
  caseId: string;
  statut: KybCaseStatus;
  decision: KybDecisionOption;
  decisionVersion: number;
  cbsOpeningState: KybCbsOpeningState | null;
  decidedBy: string;
  decidedAt: string | null;
};

type KybCbsOpeningDto = {
  decisionVersion: number;
  status: KybCbsPushStatus;
  attempts: number;
  nextAttemptAt: string | null;
  lastError: string | null;
  cbsReference: string | null;
  pushedAt: string | null;
};

type KybAuditEntryDto = {
  entryId: string;
  fieldCode: string;
  fieldLabel: string;
  oldValue: string | null;
  newValue: string | null;
  critical: boolean;
  justification: string | null;
  source: string;
  sourceLabel: string;
  actor: string;
  changedAt: string;
};

type KybAuditTimelineDto = {
  entries: KybAuditEntryDto[];
  total: number;
  page: number;
  size: number;
};

type KybLegalEntityCaseRefDto = {
  caseId: string;
};

type KybDirectorFallbackDto = {
  fallbackId: string;
  relatedPersonId: string;
  directorLabel: string | null;
  justification: string;
  status: KybDirectorFallback["status"];
  statusLabel: string | null;
  covering: boolean;
  submittedBy: string | null;
  submittedAt: string | null;
  decidedBy: string | null;
  decidedAt: string | null;
  decisionReason: string | null;
};

const mapDirectorFallback = (dto: KybDirectorFallbackDto): KybDirectorFallback => ({
  fallbackId: dto.fallbackId,
  relatedPersonId: dto.relatedPersonId,
  directorLabel: dto.directorLabel ?? undefined,
  justification: dto.justification,
  status: dto.status,
  statusLabel: dto.statusLabel ?? undefined,
  covering: dto.covering,
  submittedBy: dto.submittedBy ?? undefined,
  submittedAt: dto.submittedAt ?? undefined,
  decidedBy: dto.decidedBy ?? undefined,
  decidedAt: dto.decidedAt ?? undefined,
  decisionReason: dto.decisionReason ?? undefined,
});

const orUndefined = <T>(value: T | null | undefined): T | undefined =>
  value ?? undefined;

const mapCase = (dto: KybCaseDto): KybCaseDetail => ({
  caseId: dto.caseId,
  countryCode: dto.countryCode,
  legalFormCode: dto.legalFormCode,
  status: dto.statut,
  version: dto.version,
  createdBy: orUndefined(dto.createdBy),
  createdAt: orUndefined(dto.createdAt),
  submittedAt: orUndefined(dto.submittedAt),
  submittedBy: orUndefined(dto.submittedBy),
  beNonVerifie: dto.beNonVerifie,
  structureOpaque: dto.structureOpaque,
  readyForValidation: dto.readyForValidation,
  slaPausedAt: orUndefined(dto.slaPausedAt),
  slaResumedAt: orUndefined(dto.slaResumedAt),
  cbsOpeningState: orUndefined(dto.cbsOpeningState),
  eddRequired: dto.eddRequired ?? false,
  eddRiskAssessment: orUndefined(dto.eddRiskAssessment),
});

type InstructionSummaryDto = {
  assignee?: string | null;
  noteCount: number;
  hitConclusionCount: number;
  readyBy?: string | null;
  readyAt?: string | null;
  l1ValidatedBy?: string | null;
  l1ValidatedAt?: string | null;
};

const mapInstructionSummary = (dto: InstructionSummaryDto): KybInstructionSummary => ({
  assignee: orUndefined(dto.assignee),
  noteCount: dto.noteCount,
  hitConclusionCount: dto.hitConclusionCount,
  readyBy: orUndefined(dto.readyBy),
  readyAt: orUndefined(dto.readyAt),
  l1ValidatedBy: orUndefined(dto.l1ValidatedBy),
  l1ValidatedAt: orUndefined(dto.l1ValidatedAt),
});

type ComplementDocumentTypeDto = {
  code: string;
  label: string;
  active: boolean;
  sortOrder: number;
  updatedAt?: string | null;
  updatedBy?: string | null;
};

const mapComplementDocumentType = (dto: ComplementDocumentTypeDto): KybComplementDocumentType => ({
  code: dto.code,
  label: dto.label,
  active: dto.active,
  sortOrder: dto.sortOrder,
  updatedAt: orUndefined(dto.updatedAt),
  updatedBy: orUndefined(dto.updatedBy),
});

type AssignableAnalystsDto = {
  caseBranchCode?: string | null;
  caseBranchName?: string | null;
  analysts: {
    login: string;
    fullName: string;
    email?: string | null;
    branchCode?: string | null;
    branchName?: string | null;
    scope: "CASE_BRANCH" | "HEAD_OFFICE" | "OTHER_BRANCH";
  }[];
};

const mapAssignableAnalysts = (dto: AssignableAnalystsDto): KybAssignableAnalysts => ({
  caseBranchCode: orUndefined(dto.caseBranchCode),
  caseBranchName: orUndefined(dto.caseBranchName),
  analysts: dto.analysts.map((analyst) => ({
    login: analyst.login,
    fullName: analyst.fullName,
    email: orUndefined(analyst.email),
    branchCode: orUndefined(analyst.branchCode),
    branchName: orUndefined(analyst.branchName),
    scope: analyst.scope,
  })),
});

const mapInstruction = (dto: KybInstructionDto): KybInstructionState => ({
  caseId: dto.caseId,
  status: dto.statut,
  readyForValidation: dto.readyForValidation,
  readyBy: orUndefined(dto.readyBy),
  slaPausedAt: orUndefined(dto.slaPausedAt),
  slaResumedAt: orUndefined(dto.slaResumedAt),
});

const mapCbsOpening = (dto: KybCbsOpeningDto): KybCbsOpening => ({
  decisionVersion: dto.decisionVersion,
  status: dto.status,
  attempts: dto.attempts,
  nextAttemptAt: orUndefined(dto.nextAttemptAt),
  lastError: orUndefined(dto.lastError),
  cbsReference: orUndefined(dto.cbsReference),
  pushedAt: orUndefined(dto.pushedAt),
});

const mapAuditEntry = (dto: KybAuditEntryDto): KybCaseAuditEntry => ({
  entryId: dto.entryId,
  fieldCode: dto.fieldCode,
  fieldLabel: dto.fieldLabel,
  oldValue: orUndefined(dto.oldValue),
  newValue: orUndefined(dto.newValue),
  critical: dto.critical,
  justification: orUndefined(dto.justification),
  source: dto.source,
  sourceLabel: dto.sourceLabel,
  actor: dto.actor,
  changedAt: dto.changedAt,
});

const stringList = (value: unknown): string[] | null =>
  Array.isArray(value) && value.every((item): item is string => typeof item === "string")
    ? value
    : null;

/**
 * Le 400 d'une soumission bloquée arrive comme `HttpException` de type BAD_REQUEST dont le corps
 * (`{ code, message, data }`) porte la liste des codes manquants dans `data`. Tout autre 400
 * (corps invalide) n'a pas de liste : on rend `null` pour laisser le chemin d'erreur générique.
 */
function extractSubmissionBlockers(error: unknown): KybSubmissionBlocker[] | null {
  if (typeof error !== "object" || error === null) {
    return null;
  }
  const { type, body } = error as {
    type?: unknown;
    body?: { data?: unknown } | null;
  };
  if (type !== DefaultHttpExceptionType.BAD_REQUEST) {
    return null;
  }
  const codes = stringList(body?.data);
  return codes && codes.length > 0 ? (codes as KybSubmissionBlocker[]) : null;
}

/**
 * Le 409 d'une approbation bloquée est rejeté par `DefaultHttpClient` sous la forme
 * `{ code, message, data, type: "CONFLICT" }` (voir `extractDuplicateCandidates` pour les
 * représentants). Un 409 sans liste (dossier pas en revue de conformité) n'est pas un blocage de
 * garde : `null`, et l'erreur générique s'applique.
 */
function extractDecisionBlockers(error: unknown): KybDecisionBlocker[] | null {
  if (typeof error !== "object" || error === null) {
    return null;
  }
  const { type, data } = error as { type?: unknown; data?: unknown };
  if (type !== "CONFLICT") {
    return null;
  }
  const codes = stringList(data);
  return codes && codes.length > 0 ? (codes as KybDecisionBlocker[]) : null;
}

function isNotFound(error: unknown): boolean {
  return (
    typeof error === "object" &&
    error !== null &&
    (error as { type?: unknown }).type === DefaultHttpExceptionType.NOT_FOUND
  );
}

export default class KybCaseRepositoryBackend implements KybCaseRepository {
  constructor(private readonly httpClient: HttpClient) {}

  async getActiveCase(
    businessKey: string,
  ): Promise<RepositoryResult<KybCaseDetail>> {
    return executeKybRequest(
      () =>
        this.httpClient.get<ApiResponse<KybCaseDto>>({
          url: kybEndpoints.caseByCustomer(businessKey),
          withAuth: true,
        }),
      mapCase,
    );
  }

  async getSubmissionReadiness(businessKey: string): Promise<RepositoryResult<KybSubmissionReadiness>> {
    return executeKybRequest(
      async () => {
        const caseId = (await this.getActiveCase(businessKey)).data?.caseId;
        if (!caseId) {
          throw new Error("Impossible de lire les prérequis : le dossier KYB est introuvable.");
        }
        return this.httpClient.get<ApiResponse<KybSubmissionReadiness>>({
          url: kybEndpoints.submissionReadiness(caseId),
          withAuth: true,
        });
      },
      (dto) => ({ status: dto.status, submittable: dto.submittable, alreadySubmitted: dto.alreadySubmitted, items: dto.items }),
    );
  }

  async submitCase(
    businessKey: string,
  ): Promise<RepositoryResult<KybSubmissionOutcome>> {
    try {
      // Même résolution que les autres gestes du wizard : l'ouverture est idempotente.
      const opened = await this.httpClient.post<
        ApiResponse<KybLegalEntityCaseRefDto>,
        { businessKey: string }
      >({
        url: kybEndpoints.openOrGetCase(),
        body: { businessKey },
        withAuth: true,
      });
      const response = await this.httpClient.post<
        ApiResponse<KybSubmissionDto>,
        undefined
      >({
        url: kybEndpoints.submitCase(opened.data.caseId),
        withAuth: true,
      });
      const dto = response.data;
      return RepositoryResultFactory.success({
        outcome: "submitted",
        result: {
          caseId: dto.caseId,
          status: dto.statut,
          alreadySubmitted: dto.alreadySubmitted,
          beNonVerifie: dto.beNonVerifie,
          structureOpaque: dto.structureOpaque,
          missingMandatoryDocuments: dto.missingMandatoryDocuments,
          submittedAt: orUndefined(dto.submittedAt),
          submittedBy: orUndefined(dto.submittedBy),
        },
      });
    } catch (error) {
      const blockers = extractSubmissionBlockers(error);
      if (blockers) {
        return RepositoryResultFactory.success({
          outcome: "blocked",
          blockers,
        });
      }
      return RepositoryResultFactory.error(
        toKybException(error),
      );
    }
  }

  async addInstructionComment(
    caseId: string,
    comment: string,
  ): Promise<RepositoryResult<KybInstructionState>> {
    return executeKybRequest(
      () =>
        this.httpClient.post<ApiResponse<KybInstructionDto>, { comment: string }>({
          url: kybEndpoints.instructionComments(caseId),
          body: { comment },
          withAuth: true,
        }),
      mapInstruction,
    );
  }

  async listComplementDocumentTypes(includeInactive: boolean): Promise<RepositoryResult<KybComplementDocumentType[]>> {
    return executeKybRequest(
      () =>
        this.httpClient.get<ApiResponse<ComplementDocumentTypeDto[]>>({
          url: kybEndpoints.complementDocumentTypes(includeInactive),
          withAuth: true,
        }),
      (dtos) => dtos.map(mapComplementDocumentType),
    );
  }

  async createComplementDocumentType(input: {
    code: string;
    label: string;
    sortOrder?: number;
  }): Promise<RepositoryResult<KybComplementDocumentType>> {
    return executeKybRequest(
      () =>
        this.httpClient.post<ApiResponse<ComplementDocumentTypeDto>, typeof input>({
          url: kybEndpoints.complementDocumentTypes(false),
          body: input,
          withAuth: true,
        }),
      mapComplementDocumentType,
    );
  }

  async updateComplementDocumentType(
    code: string,
    input: { label: string; sortOrder?: number },
  ): Promise<RepositoryResult<KybComplementDocumentType>> {
    return executeKybRequest(
      () =>
        this.httpClient.put<ApiResponse<ComplementDocumentTypeDto>, typeof input>({
          url: kybEndpoints.complementDocumentType(code),
          body: input,
          withAuth: true,
        }),
      mapComplementDocumentType,
    );
  }

  async setComplementDocumentTypeActive(
    code: string,
    active: boolean,
  ): Promise<RepositoryResult<KybComplementDocumentType>> {
    return executeKybRequest(
      () =>
        this.httpClient.patch<ApiResponse<ComplementDocumentTypeDto>, { active: boolean }>({
          url: kybEndpoints.complementDocumentTypeStatus(code),
          body: { active },
          withAuth: true,
        }),
      mapComplementDocumentType,
    );
  }

  async getInstructionSummary(caseId: string): Promise<RepositoryResult<KybInstructionSummary>> {
    return executeKybRequest(
      () =>
        this.httpClient.get<ApiResponse<InstructionSummaryDto>>({
          url: kybEndpoints.instructionSummary(caseId),
          withAuth: true,
        }),
      mapInstructionSummary,
    );
  }

  async listAssignableAnalysts(caseId: string): Promise<RepositoryResult<KybAssignableAnalysts>> {
    return executeKybRequest(
      () =>
        this.httpClient.get<ApiResponse<AssignableAnalystsDto>>({
          url: kybEndpoints.instructionAnalysts(caseId),
          withAuth: true,
        }),
      mapAssignableAnalysts,
    );
  }

  async assignInstruction(
    caseId: string,
    assignee: string,
  ): Promise<RepositoryResult<KybInstructionState>> {
    return executeKybRequest(
      () =>
        this.httpClient.post<ApiResponse<KybInstructionDto>, { assignee: string }>({
          url: kybEndpoints.instructionAssignment(caseId),
          body: { assignee },
          withAuth: true,
        }),
      mapInstruction,
    );
  }

  async requestComplement(
    caseId: string,
    input: KybComplementRequestInput,
  ): Promise<RepositoryResult<KybInstructionState>> {
    return executeKybRequest(
      () =>
        this.httpClient.post<
          ApiResponse<KybInstructionDto>,
          KybComplementRequestInput
        >({
          url: kybEndpoints.instructionComplementRequests(caseId),
          body: input,
          withAuth: true,
        }),
      mapInstruction,
    );
  }

  async markReadyForValidation(
    caseId: string,
  ): Promise<RepositoryResult<KybInstructionState>> {
    return executeKybRequest(
      () =>
        this.httpClient.post<ApiResponse<KybInstructionDto>, undefined>({
          url: kybEndpoints.instructionReady(caseId),
          withAuth: true,
        }),
      mapInstruction,
    );
  }

  async reopenInstruction(
    caseId: string,
    reason: string,
  ): Promise<RepositoryResult<KybInstructionState>> {
    return executeKybRequest(
      () =>
        this.httpClient.post<ApiResponse<KybInstructionDto>, { reason: string }>({
          url: kybEndpoints.instructionReopen(caseId),
          body: { reason },
          withAuth: true,
        }),
      mapInstruction,
    );
  }

  async requestValidationMfaChallenge(
    caseId: string,
  ): Promise<RepositoryResult<KybMfaChallenge>> {
    return executeKybRequest(
      () =>
        this.httpClient.post<ApiResponse<KybMfaChallengeDto>, undefined>({
          url: kybEndpoints.instructionValidationMfaChallenge(caseId),
          withAuth: true,
        }),
      (dto) => ({ sentTo: dto.sentTo, ttlMinutes: dto.ttlMinutes, testCode: dto.testCode }),
    );
  }

  async validateCase(
    caseId: string,
    input: KybValidationInput,
  ): Promise<RepositoryResult<KybValidationResult>> {
    return executeKybRequest(
      () =>
        this.httpClient.post<ApiResponse<KybValidationDto>, KybValidationInput>({
          url: kybEndpoints.instructionValidation(caseId),
          body: input,
          withAuth: true,
        }),
      (dto) => ({
        caseId: dto.caseId,
        status: dto.statut,
        validatedBy: dto.validatedBy,
        validatedAt: orUndefined(dto.validatedAt),
        alreadyValidated: dto.alreadyValidated,
      }),
    );
  }

  async getDecisionEligibility(
    caseId: string,
  ): Promise<RepositoryResult<KybDecisionEligibility>> {
    return executeKybRequest(
      () =>
        this.httpClient.get<ApiResponse<KybEligibilityDto>>({
          url: kybEndpoints.decisionEligibility(caseId),
          withAuth: true,
        }),
      (dto) => ({
        blockers: dto.blockers.map((blocker) => ({
          code: blocker.code,
          label: blocker.label,
        })),
        allowedOptions: dto.allowedOptions,
      }),
    );
  }

  async requestDecisionMfaChallenge(
    caseId: string,
  ): Promise<RepositoryResult<KybMfaChallenge>> {
    return executeKybRequest(
      () =>
        this.httpClient.post<ApiResponse<KybMfaChallengeDto>, undefined>({
          url: kybEndpoints.decisionMfaChallenge(caseId),
          withAuth: true,
        }),
      (dto) => ({ sentTo: dto.sentTo, ttlMinutes: dto.ttlMinutes, testCode: dto.testCode }),
    );
  }

  async decideCase(
    caseId: string,
    input: KybCaseDecisionInput,
  ): Promise<RepositoryResult<KybCaseDecisionOutcome>> {
    try {
      const response = await this.httpClient.post<
        ApiResponse<KybDecisionDto>,
        KybCaseDecisionInput
      >({
        url: kybEndpoints.decision(caseId),
        body: input,
        withAuth: true,
      });
      const dto = response.data;
      const result: KybCaseDecisionResult = {
        caseId: dto.caseId,
        status: dto.statut,
        decision: dto.decision,
        decisionVersion: dto.decisionVersion,
        cbsOpeningState: orUndefined(dto.cbsOpeningState),
        decidedBy: dto.decidedBy,
        decidedAt: orUndefined(dto.decidedAt),
      };
      return RepositoryResultFactory.success({ outcome: "decided", result });
    } catch (error) {
      const blockers = extractDecisionBlockers(error);
      if (blockers) {
        return RepositoryResultFactory.success({
          outcome: "blocked",
          blockers,
        });
      }
      return RepositoryResultFactory.error(
        toKybException(error),
      );
    }
  }

  async listScreeningHits(caseId: string): Promise<RepositoryResult<KybScreeningHit[]>> {
    return executeKybRequest(
      () =>
        this.httpClient.get<ApiResponse<KybScreeningHitDto[]>>({
          url: kybEndpoints.screeningHits(caseId),
          withAuth: true,
        }),
      (dtos) => dtos.map(mapScreeningHit),
    );
  }

  async getRisk(caseId: string): Promise<RepositoryResult<KybRisk>> {
    return executeKybRequest(
      () => this.httpClient.get<ApiResponse<KybRisk>>({ url: kybEndpoints.risk(caseId), withAuth: true }),
      (dto) => ({ ...dto, score: Number(dto.score), factors: dto.factors ?? [], overrides: dto.overrides ?? [] }),
    );
  }

  async getComplementRequests(caseId: string): Promise<RepositoryResult<KybComplementRequest[]>> {
    return executeKybRequest(
      () => this.httpClient.get<ApiResponse<KybComplementRequest[]>>({ url: kybEndpoints.complementRequests(caseId), withAuth: true }),
      (rows) => (rows ?? []).map((row) => ({ ...row, pieces: row.pieces ?? [] })),
    );
  }

  async getRiskSnapshots(caseId: string): Promise<RepositoryResult<KybRiskSnapshot[]>> {
    return executeKybRequest(
      () => this.httpClient.get<ApiResponse<KybRiskSnapshot[]>>({ url: kybEndpoints.riskSnapshots(caseId), withAuth: true }),
      (rows) => (rows ?? []).map((row) => ({ ...row, score: Number(row.score) })),
    );
  }

  async getRiskConfig(): Promise<RepositoryResult<KybRiskConfigView>> {
    return executeKybRequest(
      () => this.httpClient.get<ApiResponse<KybRiskConfigView>>({ url: kybEndpoints.riskConfig(), withAuth: true }),
      (dto) => ({ config: dto.config, history: dto.history ?? [] }),
    );
  }

  async saveRiskConfig(config: KybRiskConfig, reason: string): Promise<RepositoryResult<KybRiskConfigView>> {
    return executeKybRequest(
      () =>
        this.httpClient.put<ApiResponse<KybRiskConfigView>, { config: KybRiskConfig; reason: string }>({
          url: kybEndpoints.riskConfig(),
          body: { config, reason },
          withAuth: true,
        }),
      (dto) => ({ config: dto.config, history: dto.history ?? [] }),
    );
  }

  async getScreeningSummary(caseId: string): Promise<RepositoryResult<KybScreeningSummary>> {
    return executeKybRequest(
      () =>
        this.httpClient.get<ApiResponse<KybScreeningSummary>>({
          url: kybEndpoints.screeningSummary(caseId),
          withAuth: true,
        }),
      (dto) => ({ entity: dto.entity ?? null, persons: dto.persons ?? [] }),
    );
  }

  async listPersonVerifications(caseId: string): Promise<RepositoryResult<KybPersonVerification[]>> {
    return executeKybRequest(
      () =>
        this.httpClient.get<ApiResponse<KybPersonVerification[]>>({
          url: kybEndpoints.personVerifications(caseId),
          withAuth: true,
        }),
      (rows) => rows.map((row) => ({ ...row })),
    );
  }

  async verifyPerson(
    caseId: string,
    relatedPersonId: string,
    source: string,
    proofDocumentId: string,
  ): Promise<RepositoryResult<KybPersonVerification>> {
    return executeKybRequest(
      () =>
        this.httpClient.post<ApiResponse<KybPersonVerification>, { source: string; proofDocumentId: string }>({
          url: kybEndpoints.personVerification(caseId, relatedPersonId),
          body: { source, proofDocumentId },
          withAuth: true,
        }),
      (row) => ({ ...row }),
    );
  }

  async uploadPersonProof(
    caseId: string,
    relatedPersonId: string,
    file: File,
  ): Promise<RepositoryResult<{ personDocumentId: string }>> {
    return executeKybRequest(
      () => {
        const formData = new FormData();
        formData.append("file", file);
        return this.httpClient.post<ApiResponse<{ personDocumentId: string }>, FormData>({
          url: kybEndpoints.personVerificationProof(caseId, relatedPersonId),
          body: formData,
          withAuth: true,
        });
      },
      (dto) => ({ personDocumentId: dto.personDocumentId }),
    );
  }

  async closeCase(
    caseId: string,
    justification: string,
  ): Promise<RepositoryResult<{ caseId: string; status: string }>> {
    return executeKybRequest(
      () =>
        this.httpClient.post<ApiResponse<{ caseId: string; statut: string }>, { justification: string }>({
          url: kybEndpoints.closure(caseId),
          body: { justification },
          withAuth: true,
        }),
      (dto) => ({ caseId: dto.caseId, status: dto.statut }),
    );
  }

  async qualifyScreeningHit(
    caseId: string,
    relatedPersonId: string,
    input: KybScreeningQualificationInput,
  ): Promise<RepositoryResult<KybScreeningHit>> {
    return executeKybRequest(
      () =>
        this.httpClient.post<ApiResponse<KybScreeningHitDto>, KybScreeningQualificationInput>({
          url: kybEndpoints.screeningHitQualification(caseId, relatedPersonId),
          body: input,
          withAuth: true,
        }),
      mapScreeningHit,
    );
  }

  async getEntityScreeningHit(caseId: string): Promise<RepositoryResult<KybScreeningHit | null>> {
    try {
      const response = await this.httpClient.get<ApiResponse<KybEntityHitDto | null>>({
        url: kybEndpoints.entityScreeningHit(caseId),
        withAuth: true,
      });
      const result = ApiResponseHandler.handleResponse<KybEntityHitDto | null>(response);
      if (result.success) {
        // `data` vide : aucun hit ne demande de conclusion, ce n'est pas une erreur.
        return RepositoryResultFactory.success(result.data ? mapEntityHit(result.data) : null);
      }
      return RepositoryResultFactory.error(ApiResponseHandler.createExceptionFromResponse(response));
    } catch (error) {
      return RepositoryResultFactory.error(toKybException(error));
    }
  }

  async qualifyEntityScreeningHit(
    caseId: string,
    input: KybScreeningQualificationInput,
  ): Promise<RepositoryResult<KybScreeningHit>> {
    return executeKybRequest(
      () =>
        this.httpClient.post<ApiResponse<KybEntityHitDto>, KybScreeningQualificationInput>({
          url: kybEndpoints.entityScreeningHitQualification(caseId),
          body: input,
          withAuth: true,
        }),
      mapEntityHit,
    );
  }

  async getCbsOpening(
    caseId: string,
  ): Promise<RepositoryResult<KybCbsOpening | null>> {
    try {
      const response = await this.httpClient.get<ApiResponse<KybCbsOpeningDto>>({
        url: kybEndpoints.decisionCbsOpening(caseId),
        withAuth: true,
      });
      return RepositoryResultFactory.success(mapCbsOpening(response.data));
    } catch (error) {
      // Aucune ouverture en file (dossier pas encore approuvé, ou push éteint) : état normal.
      if (isNotFound(error)) {
        return RepositoryResultFactory.success(null);
      }
      return RepositoryResultFactory.error(
        toKybException(error),
      );
    }
  }

  async retryCbsOpening(
    caseId: string,
  ): Promise<RepositoryResult<KybCbsOpening>> {
    return executeKybRequest(
      () =>
        this.httpClient.post<ApiResponse<KybCbsOpeningDto>, undefined>({
          url: kybEndpoints.decisionCbsOpeningRetry(caseId),
          withAuth: true,
        }),
      mapCbsOpening,
    );
  }

  async getDirectorFallback(
    caseId: string,
  ): Promise<RepositoryResult<KybDirectorFallback | null>> {
    try {
      const response = await this.httpClient.get<ApiResponse<KybDirectorFallbackDto>>({
        url: kybEndpoints.directorFallback(caseId),
        withAuth: true,
      });
      return RepositoryResultFactory.success(mapDirectorFallback(response.data));
    } catch (error) {
      // Aucun repli en cours : état normal pour un dossier qui a des bénéficiaires effectifs.
      if (isNotFound(error)) {
        return RepositoryResultFactory.success(null);
      }
      return RepositoryResultFactory.error(toKybException(error));
    }
  }

  async decideDirectorFallback(
    caseId: string,
    approved: boolean,
    reason?: string,
  ): Promise<RepositoryResult<KybDirectorFallback>> {
    return executeKybRequest(
      () =>
        this.httpClient.post<
          ApiResponse<KybDirectorFallbackDto>,
          { approved: boolean; reason?: string }
        >({
          url: kybEndpoints.directorFallbackDecision(caseId),
          body: { approved, reason },
          withAuth: true,
        }),
      mapDirectorFallback,
    );
  }

  async listAuditFields(caseId: string): Promise<RepositoryResult<KybAuditFieldOption[]>> {
    return executeKybRequest(
      () =>
        this.httpClient.get<ApiResponse<KybAuditFieldOption[]>>({
          url: kybEndpoints.auditFields(caseId),
          withAuth: true,
        }),
      (dtos) => dtos.map((dto) => ({ code: dto.code, label: dto.label, critical: dto.critical })),
    );
  }

  async getTimeline(
    caseId: string,
    page?: number,
    size?: number,
    filters?: KybAuditFilters,
  ): Promise<RepositoryResult<KybCaseTimeline>> {
    return executeKybRequest(
      () =>
        this.httpClient.get<ApiResponse<KybAuditTimelineDto>>({
          url: kybEndpoints.caseAudit(caseId, page, size, filters),
          withAuth: true,
        }),
      (dto) => ({
        entries: dto.entries.map(mapAuditEntry),
        total: dto.total,
        page: dto.page,
        size: dto.size,
      }),
    );
  }
}
